import React from 'react';
import { useDispatch } from 'react-redux';
import { BodyText, Button, CaptionText } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import { Message } from '@paypalcorp/worldready-react';
import _isEmpty from 'lodash/isEmpty';
import ContactOptionIcon from './ContactOptionIcon';
import { contactOptionIcons } from '../../common/constants';
import { FAQ_ARTICLE, FPTI_DATA, TOPIC_INTENT_OBJECT } from '../../common/propTypes';
import { generateLinkData, trackClickEvent } from '../../helpers/analytics/fptiHelper';
import { getArticleLink, getCommunityUrl, getResolutionURL, isLoggedIn } from '../../helpers/twHelper';
import { handleChatbotWindowOpen } from '../../helpers/chatBotHelper';
import { chatBotClickedAction } from '../../../js/contactUs/actions/action-chat-bot';
import { requestLoggedOutChatValidation } from '../../../js/lib/loggedOutChatHelper';
import { loadModalContainer } from '../../../js/contactUs/actions/action-login-modal';
import { getModalChannelType } from '../../../js/lib/globalHelper';
import { fetchPhoneNumber } from '../../../js/contactUs/actions/action-call-us';
import { modalChannels } from '../../../js/contactUs/constants/UtilVariables';
import { getElmoExperiments } from '../../../js/home/actions/action-app';
import { elmoExperimentResourceNames } from '../../../js/lib/experiments-helper';
import { isSuppressGuestCallUsLink } from '../../../js/lib/covidHelper';
import { initiateRiskCheck } from '../../../js/contactUs/actions/action-step-up';

const contactUsTextType = {
  HEADER: 'header',
  LABEL: 'label',
  TEXT_CONTENT: 'textContent',
};

const target = '_blank';

function ContactOption(props) {
  const {
    type,
    article,
    FPTIData,
    link,
    chatBotTopicRefId,
    topicName,
    isChatBotAsyncUser,
    chatPillOcpParams,
    selectedFinalIntent,
  } = props;
  const dispatch = useDispatch();

  const { articleId, articleTitle } = { ...article };

  const renderContactOptionMessage = (textType) => {
    switch (type) {
      case contactOptionIcons.MESSAGE:
      case contactOptionIcons.MESSAGE_US:
      case contactOptionIcons.CHAT:
        return <Message id={`smarthelp.app.contactOption.messageUs.${textType}`} />;
      case contactOptionIcons.CALLUS:
      case contactOptionIcons.PHONE:
        return <Message id={`smarthelp.app.contactOption.callUs.${textType}`} />;
      case contactOptionIcons.COMMUNITY:
        return <Message id={`smarthelp.app.contactOption.community.${textType}`} />;
      case contactOptionIcons.RES_CENTER:
      case contactOptionIcons.SELLER:
        return <Message id={`smarthelp.app.contactOption.resolutionCenter.${textType}`} />;
      case contactOptionIcons.ARTICLE:
        return <Message id={`smarthelp.app.contactOption.article.${textType}`} />;
      default:
        return null;
    }
  };

  const callUsOnClick = async () => {
    if (!isLoggedIn()) {
      // trigger callUs as guest elmo
      if (isSuppressGuestCallUsLink()) {
        await dispatch(getElmoExperiments(elmoExperimentResourceNames.callUsGuestResource));
      }
      //
      if (!_isEmpty(selectedFinalIntent)) {
        const transformedFinalIntent = {
          ...selectedFinalIntent,
          topicId: selectedFinalIntent.topic_id,
          topicName: selectedFinalIntent.topic_name,
          topicRefId: selectedFinalIntent.ref_id,
        };
        return dispatch(initiateRiskCheck(modalChannels.call, transformedFinalIntent, false));
      }
      // launch login modal for callUs
      return dispatch(loadModalContainer(modalChannels.call));
    }
    return dispatch(fetchPhoneNumber());
  };

  const articleClickEvent = () => {
    return window.open(getArticleLink(articleId, articleTitle), target);
  };

  const communityClickEvent = () => {
    return window.open(getCommunityUrl(), target);
  };

  const resolutionClickEvent = () => {
    return window.open(getResolutionURL(), target);
  };

  /**
   * open chatbot window when click on message channel
   * if loggedIn, open chatbot window
   * if loggedout, open logged in window
   */
  const messageClickEvent = () => {
    if (!isLoggedIn()) {
      // Logged out
      // Open up the login modal
      dispatch(chatBotClickedAction());
      if (chatBotTopicRefId) dispatch(requestLoggedOutChatValidation(chatBotTopicRefId));
      return dispatch(loadModalContainer(getModalChannelType(isChatBotAsyncUser)));
    }
    return handleChatbotWindowOpen({
      link,
      topicRefId: chatBotTopicRefId,
      topicName,
      isChatBotAsyncUser,
      chatPillOcpParams,
    });
  };

  // TODO: when other channel page is available, add click event handler here
  const handleClickEvent = (e) => {
    e.preventDefault();
    let channelName = type;
    // if concatenate articleId when clicking on article channel
    if (channelName === contactOptionIcons.ARTICLE) channelName = channelName.concat(`|${articleId}`);
    const fptiData = {
      ...FPTIData.contactOptionClick,
      ...{
        link: generateLinkData(FPTIData.contactOptionClick.link, { channelName }),
        event_name: generateLinkData(FPTIData.contactOptionClick.event_name, { channelName: type }),
      },
    };
    trackClickEvent(fptiData);
    switch (type) {
      case contactOptionIcons.CALLUS:
      case contactOptionIcons.PHONE:
        return callUsOnClick();
      case contactOptionIcons.ARTICLE:
        return articleClickEvent();
      case contactOptionIcons.COMMUNITY:
        return communityClickEvent();
      case contactOptionIcons.RES_CENTER:
      case contactOptionIcons.SELLER:
        return resolutionClickEvent();
      case contactOptionIcons.MESSAGE:
      case contactOptionIcons.CHAT:
      case contactOptionIcons.MESSAGE_US:
        return messageClickEvent();
      default:
        return null;
    }
  };

  return (
    <div className="sh_contact_option" data-testid="sh-contact-option">
      <ContactOptionIcon
        size="md"
        type={type}
        className="sh_contact_option__icon"
        data-testod="sh-contact-option-icon"
      />
      <div className="sh_contact_option__text" data-testid="sh-contact-option-text">
        <BodyText className="sh_contact_option__header" data-testid="sh-contact-option-header">
          {renderContactOptionMessage(contactUsTextType.HEADER)}
        </BodyText>
        <CaptionText className="sh_contact_option__text_content" data-testid="sh-contact-option-text-content">
          {renderContactOptionMessage(contactUsTextType.TEXT_CONTENT)}
        </CaptionText>
      </div>
      <Button
        size="sm"
        onClick={(e) => handleClickEvent(e)}
        className="sh_contact_option__button"
        data-testid="sh-contact-option-button"
        secondary
      >
        {renderContactOptionMessage(contactUsTextType.LABEL)}
      </Button>
    </div>
  );
}

ContactOption.propTypes = {
  type: PropTypes.string.isRequired,
  article: PropTypes.shape(FAQ_ARTICLE),
  FPTIData: PropTypes.shape(FPTI_DATA).isRequired,
  link: PropTypes.string,
  isChatBotAsyncUser: PropTypes.bool,
  chatPillOcpParams: PropTypes.string,
  chatBotTopicRefId: PropTypes.string,
  topicName: PropTypes.string,
  selectedFinalIntent: PropTypes.shape(TOPIC_INTENT_OBJECT).isRequired,
};

ContactOption.defaultProps = {
  article: null,
};

export default ContactOption;
